import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';

const ApplicationPage = ({ source = 'Main Website' }) => {
  const navigate = useNavigate();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Determine where the back button should go based on source
  const getBackUrl = () => {
    return source === 'Vital Method Page' ? '/vital-method' : '/';
  };

  const [formData, setFormData] = useState({
    age: '',
    fitnessLevel: '',
    helpAreas: [],
    otherHelpArea: '',
    sixMonthGoal: '',
    whyNow: '',
    fullName: '',
    email: '',
    phone: '',
    instagram: '',
    source: source
  });
  
  // Update source in formData when prop changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, source: source }));
  }, [source]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (area) => {
    setFormData(prev => {
      const helpAreas = prev.helpAreas.includes(area)
        ? prev.helpAreas.filter(a => a !== area)
        : [...prev.helpAreas, area];
      return { ...prev, helpAreas };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/coaching-application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit application');
      }

      const data = await response.json();
      
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // Scroll to top to see success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error submitting application:', error);
      }
      setIsSubmitting(false);
      alert('There was an error submitting your application. Please try again or email us directly at tyler@vitalfusioncoaching.com');
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center px-4 py-20">
        <div className="max-w-2xl w-full text-center">
          <div className="inline-flex items-center justify-center w-24 h-24 mb-8 rounded-full bg-gradient-to-br from-green-500/20 to-[#5F9BC7]/20 border-2 border-green-500/30">
            <CheckCircle className="w-16 h-16 text-green-400" />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Application Submitted!
          </h1>
          
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 mb-8">
            <p className="text-xl text-gray-300 mb-4 leading-relaxed">
              Thank you for applying. We'll review your information and see if one-on-one coaching is the right fit for your goals. We'll reach out soon to schedule your consultation call.
            </p>
          </div>

          <Button
            onClick={() => navigate(getBackUrl())}
            className="bg-gradient-to-r from-[#5F9BC7] to-[#4A8AB0] hover:from-[#4A8AB0] hover:to-[#5F9BC7] text-white px-8 py-4 text-lg rounded-xl font-semibold"
          >
            <ArrowLeft className="mr-2 w-5 h-5" />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(getBackUrl())}
            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-3">
            <img 
              src="/images/vf-logo.png" 
              alt="Vital Fusion Coaching Logo" 
              className="h-8 w-8 object-contain"
            />
            <span className="text-xl font-bold bg-gradient-to-r from-[#5F9BC7] to-[#FFE7AB] bg-clip-text text-transparent">
              Vital Fusion Coaching
            </span>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Apply for Coaching
          </h1>
          <p className="text-xl text-gray-300">
            Take the first step towards your transformation
          </p>
        </div>

        {/* Application Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Age */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8">
            <Label className="text-white font-medium text-lg mb-4 block">
              What is your age range? *
            </Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white/5 transition-colors cursor-pointer">
                <input
                  type="radio"
                  id="age-16-21"
                  name="age"
                  value="16-21"
                  checked={formData.age === '16-21'}
                  onChange={handleChange}
                  required
                  className="w-4 h-4 text-[#5F9BC7] bg-transparent border-white/30 focus:ring-[#5F9BC7] cursor-pointer"
                />
                <label htmlFor="age-16-21" className="text-white cursor-pointer flex-1">
                  16-21
                </label>
              </div>
              <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white/5 transition-colors cursor-pointer">
                <input
                  type="radio"
                  id="age-22-25"
                  name="age"
                  value="22-25"
                  checked={formData.age === '22-25'}
                  onChange={handleChange}
                  required
                  className="w-4 h-4 text-[#5F9BC7] bg-transparent border-white/30 focus:ring-[#5F9BC7] cursor-pointer"
                />
                <label htmlFor="age-22-25" className="text-white cursor-pointer flex-1">
                  22-25
                </label>
              </div>
              <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white/5 transition-colors cursor-pointer">
                <input
                  type="radio"
                  id="age-26-29"
                  name="age"
                  value="26-29"
                  checked={formData.age === '26-29'}
                  onChange={handleChange}
                  required
                  className="w-4 h-4 text-[#5F9BC7] bg-transparent border-white/30 focus:ring-[#5F9BC7] cursor-pointer"
                />
                <label htmlFor="age-26-29" className="text-white cursor-pointer flex-1">
                  26-29
                </label>
              </div>
              <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white/5 transition-colors cursor-pointer">
                <input
                  type="radio"
                  id="age-30"
                  name="age"
                  value="30+"
                  checked={formData.age === '30+'}
                  onChange={handleChange}
                  required
                  className="w-4 h-4 text-[#5F9BC7] bg-transparent border-white/30 focus:ring-[#5F9BC7] cursor-pointer"
                />
                <label htmlFor="age-30" className="text-white cursor-pointer flex-1">
                  30+
                </label>
              </div>
            </div>
          </div>

          {/* Fitness Level */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8">
            <Label className="text-white font-medium text-lg mb-4 block">
              What best describes your current level of fitness? *
            </Label>
            <div className="space-y-3">
              <div className="flex items-start space-x-3 p-4 rounded-lg hover:bg-white/5 transition-colors border border-white/10 cursor-pointer">
                <input
                  type="radio"
                  id="fitness-beginner"
                  name="fitnessLevel"
                  value="beginner"
                  checked={formData.fitnessLevel === 'beginner'}
                  onChange={handleChange}
                  required
                  className="w-4 h-4 mt-0.5 text-[#5F9BC7] bg-transparent border-white/30 focus:ring-[#5F9BC7] cursor-pointer flex-shrink-0"
                />
                <label htmlFor="fitness-beginner" className="text-white cursor-pointer leading-relaxed">
                  I am brand new to working out, but eager to learn.
                </label>
              </div>
              <div className="flex items-start space-x-3 p-4 rounded-lg hover:bg-white/5 transition-colors border border-white/10 cursor-pointer">
                <input
                  type="radio"
                  id="fitness-intermediate"
                  name="fitnessLevel"
                  value="intermediate"
                  checked={formData.fitnessLevel === 'intermediate'}
                  onChange={handleChange}
                  required
                  className="w-4 h-4 mt-0.5 text-[#5F9BC7] bg-transparent border-white/30 focus:ring-[#5F9BC7] cursor-pointer flex-shrink-0"
                />
                <label htmlFor="fitness-intermediate" className="text-white cursor-pointer leading-relaxed">
                  I understand the basics, and I've been working out for some time. I just need some extra guidance along my journey to accomplish my goals.
                </label>
              </div>
              <div className="flex items-start space-x-3 p-4 rounded-lg hover:bg-white/5 transition-colors border border-white/10 cursor-pointer">
                <input
                  type="radio"
                  id="fitness-experienced"
                  name="fitnessLevel"
                  value="experienced"
                  checked={formData.fitnessLevel === 'experienced'}
                  onChange={handleChange}
                  required
                  className="w-4 h-4 mt-0.5 text-[#5F9BC7] bg-transparent border-white/30 focus:ring-[#5F9BC7] cursor-pointer flex-shrink-0"
                />
                <label htmlFor="fitness-experienced" className="text-white cursor-pointer leading-relaxed">
                  I am experienced in the gym, but want to maximize my potential.
                </label>
              </div>
            </div>
          </div>

          {/* Areas of Help */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8">
            <Label className="text-white font-medium text-lg mb-2 block">
              What areas do you feel you need the most help with? *
            </Label>
            <p className="text-sm text-gray-400 mb-4">Select all that apply</p>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Checkbox 
                  id="diet"
                  checked={formData.helpAreas.includes('diet')}
                  onCheckedChange={() => handleCheckboxChange('diet')}
                  className="border-white/20"
                />
                <label htmlFor="diet" className="text-white cursor-pointer">
                  Diet/Nutrition
                </label>
              </div>
              
              <div className="flex items-center space-x-3">
                <Checkbox 
                  id="training"
                  checked={formData.helpAreas.includes('training')}
                  onCheckedChange={() => handleCheckboxChange('training')}
                  className="border-white/20"
                />
                <label htmlFor="training" className="text-white cursor-pointer">
                  Training
                </label>
              </div>
              
              <div className="flex items-center space-x-3">
                <Checkbox 
                  id="accountability"
                  checked={formData.helpAreas.includes('accountability')}
                  onCheckedChange={() => handleCheckboxChange('accountability')}
                  className="border-white/20"
                />
                <label htmlFor="accountability" className="text-white cursor-pointer">
                  Accountability
                </label>
              </div>
              
              <div className="flex items-center space-x-3">
                <Checkbox 
                  id="other"
                  checked={formData.helpAreas.includes('other')}
                  onCheckedChange={() => handleCheckboxChange('other')}
                  className="border-white/20"
                />
                <label htmlFor="other" className="text-white cursor-pointer">
                  Other
                </label>
              </div>

              {formData.helpAreas.includes('other') && (
                <div className="ml-8 mt-2">
                  <Label htmlFor="otherHelpArea" className="text-white text-sm mb-2 block">
                    If you selected other, please explain:
                  </Label>
                  <Textarea
                    id="otherHelpArea"
                    name="otherHelpArea"
                    value={formData.otherHelpArea}
                    onChange={handleChange}
                    rows={2}
                    className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#5F9BC7] transition-colors duration-200 resize-none"
                    placeholder="Please explain..."
                  />
                </div>
              )}
            </div>
          </div>

          {/* 6-12 Month Goal */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8">
            <Label htmlFor="sixMonthGoal" className="text-white font-medium text-lg mb-3 block">
              Fast-forward six to 12 months. What do you want to be able to say you achieved with your own fitness? *
            </Label>
            <Textarea
              id="sixMonthGoal"
              name="sixMonthGoal"
              value={formData.sixMonthGoal}
              onChange={handleChange}
              required
              rows={4}
              className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#5F9BC7] transition-colors duration-200 resize-none"
              placeholder="Describe your ideal outcome..."
            />
          </div>

          {/* Why Now */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8">
            <Label htmlFor="whyNow" className="text-white font-medium text-lg mb-3 block">
              Why is now the right time for you to focus on this? *
            </Label>
            <Textarea
              id="whyNow"
              name="whyNow"
              value={formData.whyNow}
              onChange={handleChange}
              required
              rows={4}
              className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#5F9BC7] transition-colors duration-200 resize-none"
              placeholder="What's motivating you to start now..."
            />
          </div>

          {/* Contact Information */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8">
            <h3 className="text-2xl font-bold text-white mb-6 pb-4 border-b border-white/10">
              Contact Information
            </h3>
            
            <div className="space-y-5">
              {/* Full Name */}
              <div>
                <Label htmlFor="fullName" className="text-white font-medium mb-2 block">
                  Full Name *
                </Label>
                <Input
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#5F9BC7] transition-colors duration-200"
                  placeholder="John Doe"
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-white font-medium mb-2 block">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#5F9BC7] transition-colors duration-200"
                  placeholder="john@example.com"
                />
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone" className="text-white font-medium mb-2 block">
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#5F9BC7] transition-colors duration-200"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              {/* Instagram */}
              <div>
                <Label htmlFor="instagram" className="text-white font-medium mb-2 block">
                  Instagram Handle <span className="text-gray-400 text-sm">(Optional)</span>
                </Label>
                <Input
                  id="instagram"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleChange}
                  className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#5F9BC7] transition-colors duration-200"
                  placeholder="@yourusername"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex flex-col items-center gap-4 pt-6">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full md:w-auto bg-gradient-to-r from-[#5F9BC7] to-[#4A8AB0] hover:from-[#4A8AB0] hover:to-[#5F9BC7] text-white px-12 py-4 text-lg rounded-xl font-semibold shadow-2xl shadow-[#5F9BC7]/40 transition-all duration-300 hover:shadow-[#5F9BC7]/60 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Submitting...
                </span>
              ) : (
                'Submit Application'
              )}
            </Button>

            <p className="text-center text-sm text-gray-400">
              We'll review your application and get back to you within 24 hours
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplicationPage;
